import { Button, Icon } from '@atoms/index';
import type { CartItem as CartItemType } from '@/types';
import { formatCurrency } from '@utils/currency';

interface CartItemProps {
  item: CartItemType;
  onIncrement: () => void;
  onDecrement: () => void;
  onRemove: () => void;
}

const CartItem = ({ item, onIncrement, onDecrement, onRemove }: CartItemProps) => {
  const itemTotal = (item.itemTotal || 0) * item.quantity;

  const productName = item.productName || item.menuItem?.name || 'Ürün';
  const productImage = item.productImage || item.menuItem?.imageUrl;
  const optionNames = item.optionNames || [];
  const removedNames = item.removedIngredientNames || [];

  return (
    <div className="flex gap-3 py-4 border-b border-gray-200 last:border-0">
      {/* Item Image */}
      {productImage && (
        <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-50 flex-shrink-0 border border-gray-200">
          <img
            src={productImage}
            alt={productName}
            className="w-full h-full object-contain object-center"
          />
        </div>
      )}

      {/* Item Details */}
      <div className="flex-1 min-w-0">
        <h4 className="font-semibold text-gray-900 mb-1.5 text-base">
          {productName}
        </h4>

        {/* Selected Options */}
        {optionNames.length > 0 && (
          <div className="text-xs text-gray-600 mb-3 space-y-1">
            {optionNames.map((name, index) => (
              <div key={index} className="flex items-center gap-1.5">
                <span className="text-primary">•</span>
                <span>{name}</span>
              </div>
            ))}
          </div>
        )}

        {removedNames.length > 0 && (
          <div className="text-xs text-red-600 mb-3 space-y-1">
            {removedNames.map((name, index) => (
              <div key={index} className="flex items-center gap-1.5 font-medium">
                <span>−</span>
                <span>{name}</span>
              </div>
            ))}
          </div>
        )}

        {/* Special Instructions */}
        {item.specialInstructions && (
          <div className="text-xs text-gray-500 italic mb-2 bg-gray-50 p-2 rounded">
            <span className="font-medium">Not: </span>
            {item.specialInstructions}
          </div>
        )}

        {/* Quantity Controls & Price */}
        <div className="flex items-center justify-between mt-3">
          {/* Quantity Controls */}
          <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
            <button
              onClick={onDecrement}
              className="w-8 h-8 flex items-center justify-center rounded-md bg-white text-gray-700 hover:bg-gray-200 hover:text-primary transition-colors shadow-sm border border-gray-200"
              aria-label="Azalt"
            >
              <Icon name="minus" size={18} className="text-gray-700" />
            </button>
            <span className="font-semibold text-gray-900 w-8 text-center text-sm">
              {item.quantity}
            </span>
            <button
              onClick={onIncrement}
              className="w-8 h-8 flex items-center justify-center rounded-md bg-white text-gray-700 hover:bg-primary hover:text-white transition-colors shadow-sm border border-gray-200"
              aria-label="Arttır"
            >
              <Icon name="plus" size={18} className="text-gray-700" />
            </button>
          </div>

          {/* Price & Remove */}
          <div className="flex items-center gap-3">
            <span className="font-bold text-gray-900 text-lg">
              {formatCurrency(itemTotal)}
            </span>
            <button
              onClick={onRemove}
              className="w-9 h-9 flex items-center justify-center rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
              aria-label="Kaldır"
            >
              <Icon name="close" size={18} className="text-red-600" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartItem;
